import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import AdminTables from "../../common/adminTables";
import { ConfirmationModal } from "../../common/modal";
import Messages from "../../common/messages";
import DeleteIcon from "../../../assets/svg/deleteIcon";

// Helper function to parse UTC dates (handles multiple formats)
const parseUTCDateForFeedback = (dateString: string): Date => {
	if (!dateString) return new Date();
	
	// Handle YYYY-MM-DD HH:mm:ss format (assumed to be UTC)
	const yyyymmddMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
	if (yyyymmddMatch) {
		const [, year, month, day, hour, minute, second] = yyyymmddMatch;
		
		const utcTime = Date.UTC(
			parseInt(year, 10),
			parseInt(month, 10) - 1, // Month is 0-indexed
			parseInt(day, 10),
			parseInt(hour, 10),
			parseInt(minute, 10),
			parseInt(second, 10)
		);
		
		return new Date(utcTime);
	}
	
	// Handle ISO format with T separator (YYYY-MM-DDTHH:mm:ss)
	const timePart = dateString.split('T')[1] || '';
	const hasTimezoneInfo = timePart.includes('Z') || timePart.includes('+') || timePart.includes('-');
	
	if (hasTimezoneInfo) {
		return new Date(dateString);
	}
	
	const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
	if (isoMatch) {
		const [, year, month, day, hour, minute, second] = isoMatch;
		
		const utcTime = Date.UTC(
			parseInt(year, 10),
			parseInt(month, 10) - 1,
			parseInt(day, 10),
			parseInt(hour, 10),
			parseInt(minute, 10),
			parseInt(second, 10)
		);
		
		return new Date(utcTime);
	}
	
	// Fallback to regular Date parsing
	return new Date(dateString);
};

// Helper function to format dates - converts UTC server time to local time
const formatDate = (dateString: string) => {
	if (!dateString) return 'N/A';
	try {
		// Parse as UTC and convert to local timezone
		const date = parseUTCDateForFeedback(dateString);
		
		if (isNaN(date.getTime())) {
			return 'Invalid Date';
		}
		
		const datePart = date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		});
		const timePart = date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true
		});
		return `${datePart} at ${timePart}`;
	} catch (error) {
		return 'Invalid Date';
	}
};

interface UserFeedbackProps {
	currentPage: number;
	setCurrentPage: (page: number) => void;
	itemsPerPage: number;
	setItemsPerPage: (items: number) => void;
	activeTab: string;
	setActiveTab: (tab: string) => void;
	feedbackView: 'pending' | 'approved';
	onDataChange?: (data: { results: any[]; total_count: number }) => void;
}

const UserFeedback: React.FC<UserFeedbackProps> = ({
	currentPage,
	setCurrentPage,
	itemsPerPage,
	setItemsPerPage,
	activeTab,
	setActiveTab,
	feedbackView,
	onDataChange,
}) => {
	const navigate = useNavigate();
	const { logout } = useAuth();
	const [formData, setFormData] = useState<{
		results: any[];
		total_count: number;
	}>({ results: [], total_count: 0 });
	const [isLoading, setIsLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [selectedRowData, setSelectedRowData] = useState<any>(null);
	const [showDetailsModal, setShowDetailsModal] = useState(false);
	const [detailsData, setDetailsData] = useState<any>(null);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [confirmAction, setConfirmAction] = useState<{
		type: 'approve' | 'reject' | 'delete' | 'deactivate' | 'activate';
		data: any;
	} | null>(null);
	const [actionLoading, setActionLoading] = useState(false);
	const [userEmailCache, setUserEmailCache] = useState<{[key: string]: string}>({});

	// Function to fetch user email by ID
	const fetchUserEmail = async (userId: string): Promise<string> => {
		// Check cache first
		if (userEmailCache[userId]) {
			return userEmailCache[userId];
		}

		try {
			const response = await createAxiosInstance().get(`/users?id=${userId}`);
			const email = response.data.email || 'Unknown User';
			
			// Cache the result
			setUserEmailCache(prev => ({
				...prev,
				[userId]: email
			}));
			
			return email;
		} catch (error) {
			console.error('Error fetching user email:', error);
			
			// Cache the error result to avoid repeated failed requests
			setUserEmailCache(prev => ({
				...prev,
				[userId]: 'Unknown User'
			}));
			
			return 'Unknown User';
		}
	};

	// Component to display user email with loading state
	const UserEmailDisplay: React.FC<{ userEmail: string }> = ({ userEmail }) => {
		const [email, setEmail] = useState<string>(userEmail);
		const [loading, setLoading] = useState<boolean>(true);

		useEffect(() => {
			if (!userEmail) {
				setEmail('N/A');
				setLoading(false);
				return;
			}
			setLoading(false)
		}, [userEmail]);

		if (loading) {
			return (
				<div className="d-flex align-items-center" style={{ minHeight: '20px' }}>
					<div className="spinner-border spinner-border-sm text-muted" role="status" style={{ width: '12px', height: '12px' }}>
						<span className="visually-hidden">Loading...</span>
					</div>
					<span className="ms-2 text-muted" style={{ fontSize: '0.8rem' }}>Loading...</span>
				</div>
			);
		}

		return (
			<div style={{ 
				maxWidth: '200px',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap'
			}}>
				{email}
			</div>
		);
	};

	// Define columns for pending feedback (summaries)
	const pendingFeedbackColumns = [
		{ accessorKey: "id", header: "ID", sortable: true },
		{ accessorKey: "feedback_type", header: "Type", sortable: true, cell: ({row}: {row: any}) => (
			<div style={{ 
				maxWidth: '200px',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap'
			}}>
				{row?.original?.feedback_type?.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'N/A'}
			</div>
		)},
		{ 
			accessorKey: "improvement_suggestion", 
			header: "Improvement Suggestion", 
			sortable: false,
			cell: ({ row }: { row: any }) => (
				<div 
					style={{ 
						cursor: 'pointer', 
						color: '#0066cc', 
						textDecoration: 'underline',
						maxWidth: '200px',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap'
					}}
					onClick={(e) => {
						e.stopPropagation();
						handleShowDetails(row.original);
					}}
					title="Click to view details"
				>
					{row.original.improvement_suggestion || 'No suggestion'}
				</div>
			)
		},
		{ accessorKey: "occurrence_count", header: "Occurrences", sortable: true },
		
		// { accessorKey: "feedback_category", header: "Category", sortable: true },
		
		{ 
			accessorKey: "review_status", 
			header: "Status", 
			sortable: true,
			cell: ({ row }: { row: any }) => {
				const status = row.original.review_status;
				let badgeClass = 'bg-secondary';
				
				if (status === 'pending') {
					badgeClass = 'bg-secondary';
				} else if (status === 'approved') {
					badgeClass = 'bg-success';
				} else if (status === 'rejected') {
					badgeClass = 'bg-danger';
				}
				
				return (
					<span className={`badge ${badgeClass}`}>
						{status}
					</span>
				);
			}
		},
		{ 
			accessorKey: "date_created", 
			header: "Date Created", 
			sortable: true,
			cell: ({ row }: { row: any }) => formatDate(row.original.date_created)
		},
		{
			id: "actions",
			header: "Actions",
			sortable: false,
			cell: ({ row }: { row: any }) => (
				<div className="d-flex gap-2">
					{row.original.review_status === 'pending' && (
						<>
							<button
								type="button"
								// className="btn btn-sm btn-success"
								title="Approve"
								onClick={(e) => {
									e.stopPropagation();
									handleApproveAction(row.original);
								}}								
								style={{
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									padding: '4px',
									borderRadius: '4px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '1.25rem',
								}}
								onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
								onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
							>
								<i 
								className="bi bi-check-circle" 
								></i>
							</button>
							<button
								type="button"
								// className="btn btn-sm btn-danger"
								title="Reject"
								onClick={(e) => {
									e.stopPropagation();
									handleRejectAction(row.original);
								}}
								
								style={{
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									padding: '4px',
									borderRadius: '4px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '1.25rem',
								}}
								onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
								onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
							>
								<i 
									className="bi bi-x-circle"
								></i>
							</button>
						</>
					)}


					<button
						onClick={(e) => {
							e.stopPropagation();
							handleDeleteAction(row.original);
						}}
						style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							padding: '4px',
							borderRadius: '4px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
						title="Delete"
						onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
						onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
					>
						<DeleteIcon />
					</button>
					{/* </button> */}
				</div>
			)
		}
	];

	// Define columns for approved feedback (global instructions)
	const approvedFeedbackColumns = [
		{ accessorKey: "id", header: "ID", sortable: true },
		{ accessorKey: "instruction_type", header: "Type", sortable: true },
		{ accessorKey: "category", header: "Category", sortable: true, cell: ({ row }: { row: any }) => (
			<div style={{ 
				maxWidth: '200px',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap'
			}}>
				{row.original.category ? row.original.category.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'N/A'}
			</div>
		)},
		{ 
			accessorKey: "instruction_text", 
			header: "Global Instruction", 
			sortable: false,
			cell: ({ row }: { row: any }) => (
				<div 
					style={{ 
						cursor: 'pointer', 
						color: '#0066cc', 
						textDecoration: 'underline',
						maxWidth: '300px',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap'
					}}
					onClick={(e) => {
						e.stopPropagation();
						handleShowApprovedDetails(row.original);
					}}
					title="Click to view details"
				>
					{row.original.instruction_text || 'No instruction'}
				</div>
			)
		},
		{ 
			accessorKey: "date_created", 
			header: "Created", 
			sortable: true,
			cell: ({ row }: { row: any }) => formatDate(row.original.date_created)
		},
		{ 
			accessorKey: "approved_by", 
			header: "Approved By", 
			sortable: true,
			cell: ({ row }: { row: any }) => (
				<UserEmailDisplay userEmail={row.original.approved_by_user?.email} />
			)
		},
		{ accessorKey: "is_active", header: "Active", sortable: true, cell: ({ row }: { row: any }) => (
			<span className={`badge ${row.original.is_active ? 'bg-success' : 'bg-secondary'}`}>
				{row.original.is_active ? 'Active' : 'Inactive'}
			</span>
		)},
		{
			id: "actions",
			header: "Actions",
			sortable: false,
			cell: ({ row }: { row: any }) => (
				<div className="d-flex gap-2">
					{row.original.is_active ? (
						<button
							type="button"
							className="btn btn-sm btn-outline-warning"
							onClick={(e) => {
								e.stopPropagation();
								handleDeactivateAction(row.original);
							}}
							title="Deactivate"
							style={{
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								padding: '4px',
								borderRadius: '4px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: '1.25rem',
							}}
							onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
							onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
						>
							<i className="bi bi-pause-circle"></i>
						</button>
					) : (
						<button
							type="button"
							className="btn btn-sm btn-outline-success"
							onClick={(e) => {
								e.stopPropagation();
								handleActivateAction(row.original);
							}}
							title="Activate"
							style={{
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								padding: '4px',
								borderRadius: '4px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: '1.25rem',
							}}
							onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
							onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
						>
							<i className="bi bi-play-circle"></i>
						</button>
					)}
					<button
						type="button"
						className="btn btn-sm btn-outline-danger"
						onClick={(e) => {
							e.stopPropagation();
							handleDeleteAction(row.original);
						}}
						title="Delete"
						style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							padding: '4px',
							borderRadius: '4px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: '1.25rem',
						}}
						onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
						onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
					>
						<DeleteIcon />
					</button>
				</div>
			)
		}
	];

	// Get appropriate columns based on feedback view
	const getColumns = () => {
		if (feedbackView === 'approved') {
			return approvedFeedbackColumns;
		}
		return pendingFeedbackColumns;
	};



	// Fetch approved feedback (global instructions)
	const getApprovedFeedback = async () => {
		try {
			const response = await createAxiosInstance().get("/admin/global-instructions", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
					active_only: false
				},
			});
			return { 
				data: { 
					results: response.data.data.instructions || [], 
					total_count: response.data.data.total_count || 0 
				} 
			};
		} catch (error: any) {
			if (error.status === 401 || error.status === 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
			return { data: { results: [], total_count: 0 } };
		}
	};

	// Fetch pending feedback
	const getPendingFeedback = async () => {
		try {
			const response = await createAxiosInstance().get("/admin/feedback/pending");
			return { 
				data: { 
					results: response.data.data.summaries || [], 
					total_count: response.data.data.total_pending || 0 
				} 
			};
		} catch (error: any) {
			if (error.status === 401 || error.status === 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
			return { data: { results: [], total_count: 0 } };
		}
	};

	// Fetch data based on feedback view and active tab
	const fetchFeedbackData = async () => {
		try {
			setIsLoading(true);
			setFormData({ results: [], total_count: 0 });

			let response;
			
			if (feedbackView === 'approved') {
				// For approved feedback, fetch global instructions
				response = await getApprovedFeedback();
			} else {
				// For pending feedback, always hit the /pending endpoint
				response = await getPendingFeedback();
			}

			setFormData(response.data);
			
			// Notify parent component of data change
			if (onDataChange) {
				onDataChange(response.data);
			}
		} catch (error: any) {
			if (error.status === 401 || error.status === 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleRowSelection = (rowData: any) => {
		setSelectedRowData(rowData);
		setShowModal(true);
	};

	const onClose = () => {
		setSelectedRowData(null);
		setShowModal(false);
	};

	// Handler for showing detailed feedback information
	const handleShowDetails = async (rowData: any) => {
		try {
			setActionLoading(true);
			const response = await createAxiosInstance().get(`/admin/feedback/${rowData.id}/details`);
			setDetailsData(response.data.data);
			setShowDetailsModal(true);
		} catch (error: any) {
			console.error('Error fetching feedback details:', error);
			if (error.status === 401 || error.status === 403) {
				logout();
				navigate("/");
			}
		} finally {
			setActionLoading(false);
		}
	};

	// Handler for showing approved feedback details
	const handleShowApprovedDetails = (rowData: any) => {
		setDetailsData(rowData);
		setShowDetailsModal(true);
	};

	// Handler for deactivate action
	const handleDeactivateAction = (rowData: any) => {
		setConfirmAction({ type: 'deactivate' as any, data: rowData });
		setShowConfirmModal(true);
	};

	// Handler for activate action
	const handleActivateAction = (rowData: any) => {
		setConfirmAction({ type: 'activate' as any, data: rowData });
		setShowConfirmModal(true);
	};

	// Handler for approve action
	const handleApproveAction = (rowData: any) => {
		setConfirmAction({ type: 'approve', data: rowData });
		setShowConfirmModal(true);
	};

	// Handler for reject action
	const handleRejectAction = (rowData: any) => {
		setConfirmAction({ type: 'reject', data: rowData });
		setShowConfirmModal(true);
	};

	// Handler for delete action
	const handleDeleteAction = (rowData: any) => {
		setConfirmAction({ type: 'delete', data: rowData });
		setShowConfirmModal(true);
	};

	// Handler for confirming actions
	const handleConfirmAction = async (notes?: string, customInstruction?: string) => {
		if (!confirmAction) return;

		try {
			setActionLoading(true);
			const { type, data } = confirmAction;

			if (type === 'approve' || type === 'reject') {
				const payload = {
					summary_id: data.id,
					action: type,
					notes: notes || '',
					...(type === 'approve' && customInstruction ? { custom_instruction: customInstruction } : {}),
					...(type === 'reject' ? { delete_related: true } : {})
				};

				await createAxiosInstance().post('/admin/feedback/review', payload);
				
				// Refresh the data
				fetchFeedbackData();
				
				// Show success message (you might want to add a toast notification here)
				console.log(`Successfully ${type}d feedback`);
			} else if (type === 'delete') {
				// For pending feedback, use the new delete endpoint
				await createAxiosInstance().delete(`/admin/feedback/${data.feedback_uuid}`);
				fetchFeedbackData();
				console.log('Successfully deleted feedback');
			} else if (type === 'deactivate') {
				// For deactivate action on global instructions
				const payload = {
					instruction_id: data.id,
					action: 'deactivate'
				};

				await createAxiosInstance().post('/admin/global-instructions/manage', payload);
				fetchFeedbackData();
				console.log('Successfully deactivated instruction');
			} else if (type === 'activate') {
				// For activate action on global instructions
				const payload = {
					instruction_id: data.id,
					action: 'activate'
				};

				await createAxiosInstance().post('/admin/global-instructions/manage', payload);
				fetchFeedbackData();
				console.log('Successfully activated instruction');
			}

			setShowConfirmModal(false);
			setConfirmAction(null);
		} catch (error: any) {
			console.error('Error performing action:', error);
			if (error.status === 401 || error.status === 403) {
				logout();
				navigate("/");
			}
		} finally {
			setActionLoading(false);
		}
	};



	useEffect(() => {
		fetchFeedbackData();
	}, [currentPage, itemsPerPage, feedbackView]);

	const modalBody = (rowData: any) => {
		if (feedbackView === 'approved') {
			// Modal for approved feedback data
			return (
				<div className="p-3">
					<div className="mb-3">
						<strong>ID:</strong> {rowData?.id}
					</div>
					<div className="mb-3">
						<strong>Instruction Type:</strong> {rowData?.instruction_type}
					</div>
					<div className="mb-3">
						<strong>Category:</strong> {rowData?.category ? rowData.category.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'N/A'}
					</div>
					<div className="mb-3">
						<strong>Global Instruction:</strong>
						<div className="mt-2 p-2 border rounded bg-light">
							{rowData?.instruction_text || 'No global instruction provided'}
						</div>
					</div>
					<div className="mb-3">
						<strong>Created:</strong> {formatDate(rowData?.date_created)}
					</div>
					<div className="mb-3">
						<strong>Approved By:</strong> <UserEmailDisplay userEmail={rowData?.approved_by_user?.email} />
					</div>
					<div className="mb-3">
						<strong>Status:</strong> <span className={`badge ${rowData?.is_active ? 'bg-success' : 'bg-secondary'}`}>
							{rowData?.is_active ? 'Active' : 'Inactive'}
						</span>
					</div>
				</div>
			);
		} else {
			// Modal for pending feedback data (summary data)
			return (
				<div className="p-3">
					<div className="mb-3">
						<strong>ID:</strong> {rowData?.id}
					</div>
					<div className="mb-3">
						<strong>Feedback Type:</strong> {rowData?.feedback_type}
					</div>
					<div className="mb-3">
						<strong>Category:</strong> {rowData?.feedback_category || 'N/A'}
					</div>
					<div className="mb-3">
						<strong>Occurrence Count:</strong> {rowData?.occurrence_count}
					</div>
					<div className="mb-3">
						<strong>Review Status:</strong> {rowData?.review_status}
					</div>
					<div className="mb-3">
						<strong>Date Created:</strong> {formatDate(rowData?.date_created)}
					</div>
					<div className="mb-3">
						<strong>Last Occurrence:</strong> {formatDate(rowData?.last_occurrence)}
					</div>
					<div className="mb-3">
						<strong>Question Pattern:</strong>
						<div className="mt-2 p-2 border rounded bg-light">
							{rowData?.question_pattern || 'No question pattern provided'}
						</div>
					</div>
					<div className="mb-3">
						<strong>Response Pattern:</strong>
						<div className="mt-2 p-2 border rounded bg-light">
							{rowData?.response_pattern || 'No response pattern provided'}
						</div>
					</div>
					<div className="mb-3">
						<strong>Improvement Suggestion:</strong>
						<div className="mt-2 p-2 border rounded bg-light" style={{
							color: 'rgb(33, 37, 41)'
						}}>
							{rowData?.improvement_suggestion || 'No improvement suggestion provided'}
						</div>
					</div>
				</div>
			);
		}
	};

	return (
		<div>
			{/* Data Table */}
			<AdminTables
				containerStyle={{
					width: "100%",
					overflowX: "auto",
					marginTop: '0.8rem'
				}}
				data={formData.results}
				columns={getColumns()}
				rowSelectionEnabled={true}
				onRowSelect={()=>{}}
				showSearch={false}
				isLoading={isLoading}
			/>

			{/* Modal for showing detailed feedback */}
			<ConfirmationModal
				show={showModal}
				onClose={onClose}
				closeText={'Close'}
				title={'Feedback Details'}
				body={modalBody(selectedRowData)}
				maxHeight={"80vh"}
				maxWidth={'60vw'}
			/>

			{/* Modal for showing detailed feedback information */}
			<ConfirmationModal
				show={showDetailsModal}
				onClose={() => {
					setShowDetailsModal(false);
					setDetailsData(null);
				}}
				closeText={'Close'}
				title={feedbackView === 'approved' ? 'Global Instruction Details' : 'Detailed Feedback Information'}
				body={
					<div className="p-3">
						{actionLoading ? (
							<div className="text-center">
								<div className="spinner-border text-primary" role="status">
									<span className="visually-hidden">Loading...</span>
								</div>
							</div>
						) : detailsData ? (
							<div>
								{feedbackView === 'approved' ? (
									// Show approved feedback details
									<div style={{
										minHeight: '50vh'
									}}>
										<div className="mb-3 text-start" style={{
											fontSize: '0.875rem',
											fontFamily: 'Roboto',
											
										}}>
											<h6>Global Instruction Information:</h6>
											<div className="border rounded bg-light">
												<div className="card-body">
													<div className="row">
														<div className="col-md-6">
															<strong>ID:</strong> <span className="text-muted">{detailsData.id}</span>
														</div>
														<div className="col-md-6">
															<strong>Type:</strong> <span className="text-muted">{detailsData.instruction_type}</span>
														</div>
													</div>
													<div className="row mt-2">
														<div className="col-md-6">
															<strong>Category:</strong> <span className="text-secondary">{detailsData.category ? detailsData.category.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'N/A'}</span>
														</div>
														<div className="col-md-6">
															<strong>Created:</strong> <span className="text-secondary">{formatDate(detailsData.date_created)}</span>
														</div>
													</div>
													<div className="row mt-2">
														<div className="col-md-6">
															<strong>Approved By:</strong> <span className="text-secondary"><UserEmailDisplay userEmail={detailsData.approved_by_user?.email} /></span>
														</div>
														<div className="col-md-6">
															<strong>Status:</strong> <span className={`badge ${detailsData.is_active ? 'bg-success' : 'bg-secondary'}`}>
																{detailsData.is_active ? 'Active' : 'Inactive'}
															</span>
														</div>
													</div>
												</div>
											</div>
										</div>

										<div className="mb-3" style={{
											fontSize: '0.875rem',
											fontFamily: 'Roboto',
										}}>
											<h6 style={{ textAlign: 'start' }}>Global Instruction:</h6>
											<div className="mt-1 p-2 border rounded bg-light" style={{
												color: 'rgb(33, 37, 41)',
												textAlign: 'start',
											}}>
												{detailsData.instruction_text || 'No instruction text available'}
											</div>
										</div>
									</div>
								) : (
									// Show pending feedback details
									<div>
										<div className="mb-3 text-start" style={{
											fontSize: '0.875rem',
											// fontWeight: 'bold',
											fontFamily: 'Roboto',
										}}>
											<h6 >Summary Information:</h6>
											<div className="border rounded bg-light">
												<div className="card-body">
													<div className="row">
														<div className="col-md-6 ">
															<strong>ID:</strong> <span className="text-muted">{detailsData.summary_info?.id}</span>
														</div>
														<div className="col-md-6">
															<strong>Type:</strong> <span className="text-muted">{detailsData.summary_info?.feedback_type}</span>
														</div>
													</div>
													<div className="row mt-2">
														<div className="col-md-6">
															<strong>Category:</strong> <span className="text-secondary">{detailsData.summary_info?.feedback_category}</span>
														</div>
														<div className="col-md-6">
															<strong>Occurrences:</strong> <span className="text-secondary">{detailsData.summary_info?.occurrence_count}</span>
														</div>
													</div>
													<div className="row mt-2">
														<div className="col-md-6">
															<strong>Status:</strong> <span className="text-secondary">{detailsData.summary_info?.review_status}</span>
														</div>
														<div className="col-md-6">
															<strong>Last Occurrence:</strong> <span className="text-secondary">{formatDate(detailsData.summary_info?.last_occurrence)}</span>
														</div>
													</div>
													{/* <div className="mt-3">
														<strong>Improvement Suggestion:</strong>
														<div className="mt-1 p-2 border rounded bg-light">
															{detailsData.summary_info?.improvement_suggestion || 'No improvement suggestion'}
														</div>
													</div> */}
												</div>
											</div>
										</div>

										<div className="mb-3" style={{
											fontSize: '0.875rem',
											// fontWeight: 'bold',
											fontFamily: 'Roboto',
										}}>
											<h6 style={{ textAlign: 'start' }}>Improvement Suggestion:</h6>
											<div className="mt-1 p-2 border rounded bg-light" style={{
												// fontSize: '0.875rem',
												color: 'rgb(33, 37, 41)',
												textAlign: 'start',
											}}>
												{detailsData.summary_info?.improvement_suggestion || 'No improvement suggestion'}
											</div>
										</div>
										
										{/* Message Component for Question and Response */}
										<div className="mb-3" style={{
											fontSize: '0.875rem', fontFamily: 'Roboto'
										}}>
											<h6 style={{ textAlign: 'start' }}>Conversation Pattern:</h6>
											<Messages
												useCase={1}
												messages={[
													{
														type: 'right',
														text: detailsData.summary_info?.question_pattern || 'No question pattern available',
														time: new Date().toLocaleTimeString(),
														icon: false,
													},
													{
														type: 'left',
														text: detailsData.summary_info?.response_pattern || 'No response pattern available',
														time: new Date().toLocaleTimeString(),
														icon: false,
													}
												]}
												chat_id=""
												handleOptionSelect={() => {}}
												handleUseCaseSelect={() => {}}
												handleSubCase={() => {}}
												showMessage={false}
												selectedSubCase=""
												setDisabled={() => {}}
												hideIcons={true}
											/>
										</div>
										
										{/* {detailsData.individual_feedbacks && detailsData.individual_feedbacks.length > 0 ? (
											<div className="mb-3">
												<h5>Individual Feedback Records ({detailsData.individual_feedbacks.length})</h5>
												<div className="mt-3">
													{detailsData.individual_feedbacks.map((feedback: any, index: number) => (
														<div key={index} className="card mb-3">
															<div className="card-body">
																<div className="row">
																	<div className="col-md-6">
																		<strong>User:</strong> {feedback.user_email || 'Anonymous'}
																	</div>
																	<div className="col-md-6">
																		<strong>Date:</strong> {feedback.created_at}
																	</div>
																</div>
																<div className="row mt-2">
																	<div className="col-md-6">
																		<strong>Chat ID:</strong> {feedback.chat_id}
																	</div>
																	<div className="col-md-6">
																		<strong>Rating:</strong> {feedback.rating || 'N/A'}
																	</div>
																</div>
																<div className="row mt-2">
																	<div className="col-12">
																		<strong>Feedback:</strong>
																		<div className="mt-1 p-2 border rounded bg-light">
																			{feedback.feedback_text}
																		</div>
																	</div>
																</div>
															</div>
														</div>
													))}
												</div>
											</div>
										) : ( */}
											<div className="mb-3" style={{
												fontSize: '0.875rem', fontFamily: 'Roboto'
											}}>
												<div className="alert alert-info">
													<strong>Summary:</strong> This feedback pattern has occurred {detailsData.summary_info?.occurrence_count || 0} times.
												</div>
											</div>
										{/* )} */}
																			</div>
									
								)}
							</div>
						) : (
							<p>No details available.</p>
						)}
					</div>
				}
				maxHeight={"80vh"}
				maxWidth={'70vw'}
			/>

			{/* Confirmation Modal for Actions */}
			<ConfirmationModal
				show={showConfirmModal}
				onClose={() => {
					setShowConfirmModal(false);
					setConfirmAction(null);
				}}
				onConfirm={() => {
					const notesElement = document.getElementById('actionNotes') as HTMLTextAreaElement;
					const customInstructionElement = document.getElementById('customInstruction') as HTMLTextAreaElement;
					const notes = notesElement?.value || '';
					const customInstruction = customInstructionElement?.value || '';
					
					if (confirmAction?.type === 'reject' && !notes.trim()) {
						alert('Notes are required for reject action');
						return;
					}
					
					handleConfirmAction(notes, customInstruction);
				}}
				closeText={'CANCEL'}
				title={
					confirmAction ? 
						`${confirmAction.type.charAt(0).toUpperCase() + confirmAction.type.slice(1)} Feedback` : 
						'Confirm Action'
				}
				color=""
				body={
					<div>
						{actionLoading ? (
							<div className="text-center py-2">
								<div className="spinner-border text-primary" role="status">
									<span className="visually-hidden">Processing...</span>
								</div>
								<p className="mt-2 text-muted">Processing your request...</p>
							</div>
						) : confirmAction ? (
							<div style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem', textAlign: 'start', fontSize: '0.875rem' }}>
								
								{confirmAction.type === 'approve' && (
									<div className="mb-3">
										<label htmlFor="customInstruction" className="form-label" style={{ fontSize: '0.875rem', color: 'rgb(33, 37, 41)' }}>
											Custom Global Instruction (Optional):
										</label>
										<textarea
											id="customInstruction"
											className="form-control bg-white"
											rows={3}
											placeholder="Enter custom instruction for global learning..."
											style={{ fontSize: '0.875rem' }}
										/>
									</div>
								)}
								
								<div className="mb-3">
									<label htmlFor="actionNotes" className="form-label" style={{ fontSize: '0.875rem', color: 'rgb(33, 37, 41);' }}>
										Notes {confirmAction.type === 'reject' ? '(Required)' : '(Optional)'}:
									</label>
									<textarea
										id="actionNotes"
										className="form-control bg-white"
										rows={2}
										placeholder={`Enter notes for this ${confirmAction.type} action...`}
										style={{ fontSize: '0.875rem' }}
									/>
								</div>
							</div>
						) : null}
					</div>
				}
				maxHeight={confirmAction?.type === 'approve' ? "43vh" : "31vh"}
				maxWidth={'50vw'}
			/>
		</div>
	);
};

export default UserFeedback; 